import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

/**
 * Subscribe to Supabase Realtime postgres_changes for `table` and
 * automatically invalidate the given React Query cache key on every
 * INSERT / UPDATE / DELETE.
 *
 * Usage:
 * ```tsx
 * useAdminRealtime("orders", ["admin", "dashboard"]);
 * ```
 *
 * The hook is safe to call with a stable `queryKey` array — it joins
 * the key into a string for the dependency check so a new array
 * reference on every render does not cause re-subscriptions.
 */
export function useAdminRealtime(
  table: string,
  queryKey: readonly string[],
): void {
  const queryClient = useQueryClient();

  // Stabilise the key reference so the effect only re-runs when the
  // *values* change, not when the caller creates a new array literal.
  const keyString = queryKey.join(",");
  const queryKeyRef = useRef(queryKey);
  queryKeyRef.current = queryKey;

  useEffect(() => {
    const channel = supabase
      .channel(`admin-${table}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table },
        () => {
          queryClient.invalidateQueries({ queryKey: [...queryKeyRef.current] });
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [table, keyString, queryClient]);
}
